import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import api, { downloadFile } from '../../utils/api';
import { 
  Archive, Download, Trash2, Upload, FolderPlus, Folder, File, 
  ChevronDown, ChevronUp, X, Edit, Save
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { useDropzone } from 'react-dropzone';
import BackButton from '../../components/BackButton';
import useAuthStore from '../../store/authStore';

// Year color coding - distinct, accessible colors
const YEAR_COLORS = {
  2026: { bg: 'bg-blue-600', border: 'border-blue-500', text: 'text-blue-100', badge: 'bg-blue-500' },
  2025: { bg: 'bg-emerald-600', border: 'border-emerald-500', text: 'text-emerald-100', badge: 'bg-emerald-500' },
  2024: { bg: 'bg-purple-600', border: 'border-purple-500', text: 'text-purple-100', badge: 'bg-purple-500' },
  2023: { bg: 'bg-orange-600', border: 'border-orange-500', text: 'text-orange-100', badge: 'bg-orange-500' },
  2022: { bg: 'bg-rose-600', border: 'border-rose-500', text: 'text-rose-100', badge: 'bg-rose-500' },
  2021: { bg: 'bg-cyan-600', border: 'border-cyan-500', text: 'text-cyan-100', badge: 'bg-cyan-500' },
  2020: { bg: 'bg-amber-600', border: 'border-amber-500', text: 'text-amber-100', badge: 'bg-amber-500' },
  default: { bg: 'bg-slate-600', border: 'border-slate-500', text: 'text-slate-100', badge: 'bg-slate-500' }
};

const getYearColor = (dateStr) => {
  const year = new Date(dateStr).getFullYear();
  return YEAR_COLORS[year] || YEAR_COLORS.default;
};

const getYearFromDate = (dateStr) => {
  return new Date(dateStr).getFullYear();
};

const Meetings = () => {
  const { user } = useAuthStore();
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedArchive, setSelectedArchive] = useState(null);
  const [expandedArchive, setExpandedArchive] = useState(null);
  
  // Form state
  const [archiveName, setArchiveName] = useState('');
  const [archiveDescription, setArchiveDescription] = useState('');
  
  // Edit state
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editingArchive, setEditingArchive] = useState(null);
  
  // Files for upload
  const [filesToUpload, setFilesToUpload] = useState([]);

  useEffect(() => {
    loadArchives();
  }, []);

  const loadArchives = async () => {
    try {
      const response = await api.get('/meetings');
      setArchives(response.data);
    } catch (error) {
      toast.error('Failed to load meeting archives');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    setFilesToUpload(prev => [...prev, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true
  });

  const handleCreateArchive = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams();
      params.append('title', archiveName);
      params.append('meeting_type', 'Archive');
      params.append('meeting_date', new Date().toISOString());
      params.append('organising_body', 'AHSGA');
      if (archiveDescription) params.append('description', archiveDescription);
      
      await api.post(`/meetings?${params.toString()}`);
      toast.success('Archive folder created successfully');
      setIsCreateDialogOpen(false);
      setArchiveName('');
      setArchiveDescription('');
      loadArchives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create archive');
    }
  };

  const handleEditArchive = async (e) => {
    e.preventDefault();
    if (!editingArchive) return;
    
    try {
      await api.put(`/meetings/${editingArchive.id}`, {
        title: editName,
        description: editDescription
      });
      toast.success('Archive updated successfully');
      setIsEditDialogOpen(false);
      setEditingArchive(null);
      loadArchives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update archive');
    }
  };

  const openEditDialog = (archive, e) => {
    e.stopPropagation();
    setEditingArchive(archive);
    setEditName(archive.title);
    setEditDescription(archive.description || '');
    setIsEditDialogOpen(true);
  };

  const handleDeleteArchive = async (archiveId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this archive and all its documents?')) {
      return;
    }
    try {
      await api.delete(`/meetings/${archiveId}`);
      toast.success('Archive deleted successfully');
      loadArchives();
    } catch (error) {
      toast.error('Failed to delete archive');
    }
  };

  const handleUploadDocuments = async () => {
    if (!selectedArchive || filesToUpload.length === 0) return;
    
    try {
      const formData = new FormData();
      filesToUpload.forEach(file => {
        formData.append('files', file);
      });
      
      await api.post(`/meetings/${selectedArchive.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`${filesToUpload.length} document(s) uploaded successfully`);
      setIsUploadDialogOpen(false);
      setFilesToUpload([]);
      setSelectedArchive(null);
      loadArchives();
    } catch (error) {
      toast.error('Failed to upload documents');
    }
  };

  const handleDeleteDocument = async (archiveId, documentId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await api.delete(`/meetings/${archiveId}/documents/${documentId}`);
      toast.success('Document deleted successfully');
      loadArchives();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = async (fileUrl, fileName, e) => {
    e.stopPropagation();
    try {
      await downloadFile(fileUrl, fileName);
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDownloadAll = async (archiveId, archiveTitle, e) => {
    e.stopPropagation();
    try {
      toast.info('Preparing download...');
      
      // Use authenticated API call with blob response
      const response = await api.get(`/meetings/${archiveId}/download-all`, {
        responseType: 'blob',
        timeout: 120000, // 2 minute timeout for large ZIPs
      });
      
      // Check if we got an error response
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Download failed');
      }
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/zip' });
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${archiveTitle.replace(/\s+/g, '_')}.zip`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      
      toast.success('Download started');
    } catch (error) {
      console.error('Download All error:', error);
      toast.error(error.message || 'Failed to download folder');
    }
  };

  const openUploadDialog = (archive, e) => {
    e.stopPropagation();
    setSelectedArchive(archive);
    setFilesToUpload([]);
    setIsUploadDialogOpen(true);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-meetings-page">
      <BackButton to="/admin" />
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-amber-100 p-3 rounded-xl">
            <Archive className="w-6 h-6 text-amber-600" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-900">Meeting Archive</h1>
            <p className="text-sm text-slate-500">Upload and manage past meeting folders</p>
          </div>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-amber-600 hover:bg-amber-700" data-testid="create-archive-btn">
              <FolderPlus className="w-4 h-4 mr-2" />
              New Archive Folder
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Archive Folder</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateArchive} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="archiveName">Folder Name *</Label>
                <Input
                  id="archiveName"
                  value={archiveName}
                  onChange={(e) => setArchiveName(e.target.value)}
                  placeholder="e.g., Board Meeting - January 2024"
                  required
                  data-testid="archive-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="archiveDescription">Description (Optional)</Label>
                <textarea
                  id="archiveDescription"
                  className="w-full min-h-[80px] p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                  value={archiveDescription}
                  onChange={(e) => setArchiveDescription(e.target.value)}
                  placeholder="Brief description of this meeting archive..."
                  data-testid="archive-desc-input"
                />
              </div>
              
              <Button type="submit" className="w-full bg-amber-600 hover:bg-amber-700" data-testid="submit-archive-btn">
                Create Folder
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Archive Folder</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditArchive} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="editName">Folder Name *</Label>
              <Input
                id="editName"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                placeholder="e.g., Board Meeting - January 2024"
                required
                data-testid="edit-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="editDescription">Description (Optional)</Label>
              <textarea
                id="editDescription"
                className="w-full min-h-[80px] p-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="Brief description of this meeting archive..."
                data-testid="edit-desc-input"
              />
            </div>
            
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1 bg-amber-600 hover:bg-amber-700" data-testid="save-edit-btn">
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </Button>
              <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Year Color Legend */}
      {archives.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center p-3 bg-slate-50 rounded-lg">
          <span className="text-xs text-slate-500 mr-2">Color by Year:</span>
          {Object.entries(YEAR_COLORS).filter(([key]) => key !== 'default').slice(0, 5).map(([year, colors]) => (
            <span key={year} className={`${colors.badge} text-white text-xs px-2 py-1 rounded-full`}>
              {year}
            </span>
          ))}
        </div>
      )}

      {/* Archives List */}
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : archives.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-xl">
          <Folder className="w-12 h-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-500">No meeting archives yet</p>
          <p className="text-sm text-slate-400">Click &quot;New Archive Folder&quot; to create one</p>
        </div>
      ) : (
        <div className="space-y-4">
          {archives.map((archive) => (
            <div key={archive.id} className={`bg-white border-2 ${getYearColor(archive.created_at).border} rounded-xl overflow-hidden shadow-sm`}>
              {/* Archive Header with Year Color Coding */}
              <div 
                className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors border-l-4 ${getYearColor(archive.created_at).border}`}
                onClick={() => setExpandedArchive(expandedArchive === archive.id ? null : archive.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className={`${getYearColor(archive.created_at).bg} p-2 rounded-lg`}>
                      <Folder className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <h3 className="font-semibold text-slate-900">{archive.title}</h3>
                        <span className={`${getYearColor(archive.created_at).badge} text-white text-xs px-2 py-0.5 rounded-full font-medium`}>
                          {getYearFromDate(archive.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-slate-500">Created: {formatDate(archive.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded">
                      {archive.documents?.length || 0} files
                    </span>
                    {/* Edit Button */}
                    <button
                      onClick={(e) => openEditDialog(archive, e)}
                      className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded transition-colors"
                      title="Edit"
                      data-testid={`edit-archive-${archive.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    {expandedArchive === archive.id ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </div>
                </div>
                {archive.description && (
                  <p className="mt-2 text-sm text-slate-600">{archive.description}</p>
                )}
              </div>
              
              {/* Expanded Content */}
              {expandedArchive === archive.id && (
                <div className="border-t border-slate-200 p-4 bg-slate-50">
                  {/* Action Buttons */}
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-slate-700">Documents</h4>
                    <div className="flex space-x-2">
                      {archive.documents?.length > 0 && (
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-green-600 hover:bg-green-50 border-green-300"
                          onClick={(e) => handleDownloadAll(archive.id, archive.title, e)}
                          data-testid={`download-all-${archive.id}`}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download All
                        </Button>
                      )}
                      <Button size="sm" className="bg-amber-600 hover:bg-amber-700" onClick={(e) => openUploadDialog(archive, e)}>
                        <Upload className="w-4 h-4 mr-1" />
                        Upload Files
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="text-red-600 hover:bg-red-50"
                        onClick={(e) => handleDeleteArchive(archive.id, e)}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete Folder
                      </Button>
                    </div>
                  </div>
                  
                  {/* Documents List */}
                  {archive.documents?.length > 0 ? (
                    <div className="space-y-2">
                      {archive.documents.map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200">
                          <div className="flex items-center space-x-3">
                            <File className="w-5 h-5 text-slate-400" />
                            <div>
                              <p className="text-sm font-medium text-slate-700">{doc.file_name}</p>
                              <p className="text-xs text-slate-500">
                                {formatFileSize(doc.file_size)} • Uploaded {formatDate(doc.upload_date)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={(e) => handleDownload(doc.file_url, doc.file_name, e)}
                              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded"
                              title="Download"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteDocument(archive.id, doc.id, e)}
                              className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">No documents uploaded yet. Click &quot;Upload Files&quot; to add documents.</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Upload to &quot;{selectedArchive?.title}&quot;</DialogTitle>
          </DialogHeader>
          
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragActive ? 'border-amber-500 bg-amber-50' : 'border-slate-300 hover:border-slate-400'
            }`}
          >
            <input {...getInputProps()} data-testid="file-upload-input" />
            <Upload className="w-10 h-10 mx-auto mb-3 text-slate-400" />
            {isDragActive ? (
              <p className="text-amber-600">Drop files here...</p>
            ) : (
              <>
                <p className="text-slate-600">Drag & drop files here, or click to select</p>
                <p className="text-sm text-slate-400 mt-1">Upload multiple files at once</p>
              </>
            )}
          </div>
          
          {filesToUpload.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              <p className="text-sm font-medium text-slate-700">{filesToUpload.length} file(s) selected:</p>
              {filesToUpload.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                  <div className="flex items-center space-x-2">
                    <File className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-600 truncate max-w-xs">{file.name}</span>
                  </div>
                  <button
                    onClick={() => setFilesToUpload(prev => prev.filter((_, i) => i !== index))}
                    className="text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          
          <Button 
            onClick={handleUploadDocuments} 
            className="w-full bg-amber-600 hover:bg-amber-700"
            disabled={filesToUpload.length === 0}
            data-testid="submit-upload-btn"
          >
            Upload {filesToUpload.length} File(s)
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Meetings;
